import { toast } from '@tamagui/toast/v2';
import {
  addMonths,
  differenceInCalendarDays,
  endOfMonth,
  format,
  startOfMonth,
} from 'date-fns';
import {
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import Head from 'expo-router/head';
import { useHeaderHeight } from 'expo-router/react-navigation';
import { type ComponentProps, useCallback, useState } from 'react';
import { XStack, YStack } from 'tamagui';

import ConfirmationPagePlanCard from '@/components/ConfirmationPagePlanCard';
import { limitLabel } from '@/components/MembershipPlanCard';
import { PSHeaderBackButton } from '@/components/PSHeaderButtons';
import PageScrollView from '@/components/ui/PageScrollView';
import { PAGE_GUTTER } from '@/components/ui/pageFrame';
import PSBottomBar from '@/components/ui/PSBottomBar';
import PSButton from '@/components/ui/PSButton';
import PSIcon from '@/components/ui/PSIcon';
import PSModal from '@/components/ui/PSModal';
import PSText from '@/components/ui/PSText';
import { asApiError } from '@/src/api/apiError';
import type { UserProfile } from '@/src/api/authApi';
import { newIdempotencyKey } from '@/src/api/idempotency';
import membershipsApi, { type Membership } from '@/src/api/membershipsApi';
import { useMembershipPlans } from '@/src/hooks/useMembershipPlans';
import { usePastMemberships } from '@/src/hooks/usePastMemberships';
import {
  canPurchase,
  derivePurchaseGate,
  isNothingDueToday,
  isProrated,
  type PurchaseGate,
} from '@/src/lib/membershipUpsell';
import { openMembershipSheet } from '@/src/lib/razorpayCheckout';
import { useInsetStyle } from '@/src/lib/safeAreaInset';
import { useSessionUser } from '@/src/lib/sessionStore';
import { formatDateWithYear } from '@/src/utils/dateFormatter';
import { space } from '@/constants/tokens';
import Divider from '@/components/ui/Divider';

/**
 * The confirm step of a pass purchase — where a card in home's upsell lands.
 *
 * It carries only the plan's `id`, and reads the plan itself out of
 * [[useMembershipPlans]]: there is no `GET /membership-plans/{id}`, so the list
 * IS the detail endpoint. That also means a plan can go missing between the tap
 * and the arrival — the catalog only returns active plans — which is what the
 * third state below is for.
 *
 * The press is PINNED, not scrolled — the purchase bar is the sibling under the
 * scroll, because the page is long enough that a CTA at its end starts below
 * the fold.
 */
export default function MembershipPurchaseConfirmPage() {
  const { id } = useLocalSearchParams<{ id: string }>();

  const { plans, isLoading, error } = useMembershipPlans();
  const user = useSessionUser();
  const pastMemberships = usePastMemberships();
  const router = useRouter();
  const [isPaying, setIsPaying] = useState(false);

  // ONE key per visit, minted on mount (the lazy initialiser runs once). The
  // header is required by `POST /membership`, and repeating the same key REPLAYS
  // the membership it created rather than authorizing a second mandate — which is
  // what makes pressing Pay again after dismissing the sheet resume the same
  // purchase instead of leaving a second pending one behind.
  const [idempotencyKey] = useState(newIdempotencyKey);

  // Set true right when Pay detours into the auth flow to fix an incomplete
  // profile — that tap IS the purchase intent, so it shouldn't be asked for
  // twice. See the focus effect below `handlePay` for the resume.
  const [pendingPurchase, setPendingPurchase] = useState(false);

  // The dark band runs up behind the notch.
  useInsetStyle('dark');

  const plan = plans?.find((candidate) => candidate.id === id) ?? null;
  // Signed out, `user` is null and this reads as "has held nothing" — which is
  // the honest answer: there's no way to know what they've spent.
  const gate = plan
    ? derivePurchaseGate({
        plans: plans ?? [],
        user,
        membershipBeingViewed: plan,
        pastMemberships: pastMemberships ?? [],
      })
    : null;
  // Null until the gate can be trusted: the plan, and — signed in — the history
  // that can close the trial.
  const isPurchasable =
    gate && pastMemberships !== null ? canPurchase(gate) : null;
  const blocked =
    plan && gate && isPurchasable !== null
      ? blockedNoticeFor(plan, gate)
      : null;

  /**
   * The whole purchase press: create the membership, collect the money, hand off
   * to the status screen — which polls `/me` until the membership goes live.
   *
   * CANCELLING is not a failure: the user chose it, so nothing is thrown and they
   * stay on this screen, where the same idempotency key resumes the SAME purchase.
   *
   * An UNFINISHED profile never gets that far: `POST /membership` refuses one, so
   * the press detours into the auth flow at the step that fixes it — the same
   * gate the event booking bar applies before its confirm step.
   *
   * Everything else — a plan the server won't sell, a membership they already
   * hold (409), a declined card — arrives in the one `catch` as a `PSErrorClass`
   * and shows its own message.
   *
   * The sheet resolving does NOT mean the money landed; only the webhook our
   * server hears does. That's the status screen's job.
   */
  const handlePay = useCallback(async () => {
    if (!plan || user === null || !isPurchasable) return;

    // Anything short of a finished profile goes to the auth step that
    // completes it, carrying a heading that says why they're there. The
    // three flags are mutually exclusive (see `toUserProfile`) — a missing
    // name chains all the way to email (`next: 'email'`), since a detour
    // here needs full completion, not just a name. `pendingPurchase`
    // remembers that Pay was already pressed, so the focus effect below
    // resumes straight into payment once the profile is complete — no need
    // to press Pay again.
    if (user.areDetailsMissing) {
      setPendingPurchase(true);
      router.push({
        pathname: '/auth/details',
        params: {
          headerCopy: 'Please complete your profile to make a purchase',
          next: 'email',
        },
      });
      return;
    }
    if (user.isEmailMissing || user.isOnlyEmailVerificationPending) {
      setPendingPurchase(true);
      router.push({
        pathname: '/auth/email',
        params: {
          headerCopy: 'Please add and verify your email to make a purchase',
        },
      });
      return;
    }

    setIsPaying(true);
    try {
      const membership = await membershipsApi.createMembership({
        planId: plan.id,
        idempotencyKey,
      });
      const { checkout } = membership;

      if (checkout) {
        const outcome = await openMembershipSheet({
          checkout,
          planName: plan.name,
          payer: {
            name: user.fullName,
            // Null only for a logged-in user whose profile isn't finished, and
            // the gate above sends those to the auth flow instead.
            email: user.email ?? '',
            contact: user.mobile,
          },
        });

        if (outcome.status === 'cancelled') {
          // Not `toast.error`: nothing broke, and red is the app's colour for a
          // failure. This is the neutral card.
          setIsPaying(false);
          return;
        }
      }

      // The MEMBERSHIP's id is what the next screen polls; the plan's id rides
      // along only so it can name what's being confirmed.
      router.replace({
        pathname: '/memberships/purchase/status',
        params: { id: membership.id, plan: plan.id },
      });
    } catch (e) {
      // Reset only on failure — on the way out, a button that flips back to
      // "Pay" mid-transition reads as if nothing happened.
      toast.error(asApiError(e).message);
      setIsPaying(false);
    }
  }, [plan, user, isPurchasable, idempotencyKey, router]);

  // `useFocusEffect` (not a plain effect): the profile can also become
  // complete while this screen ISN'T focused — e.g. via the profile screen's
  // own "complete your profile" link — and firing then would be silently
  // wrong. `pendingPurchase` is consumed on the FIRST refocus after the
  // detour regardless of outcome, so an abandoned detour can't leave it
  // sitting armed to fire `handlePay` on some later, unrelated refocus.
  //
  // Signing in can land them on a plan they can't buy, so the resume waits for a
  // gate it can trust and drops the intent when that gate says no.
  useFocusEffect(
    useCallback(() => {
      if (!pendingPurchase) return;
      if (user?.isProfileComplete && isPurchasable !== null) {
        if (isPurchasable) handlePay();
        setPendingPurchase(false);
      }
    }, [pendingPurchase, user?.isProfileComplete, isPurchasable, handlePay])
  );

  // Signed out, so this IS the purchase attempt — carry `next: 'email'` through
  // so a fresh signup chains all the way to a verified email instead of stopping
  // at a name and detouring AGAIN from `handlePay` once they're back here.
  const handleLogin = useCallback(() => {
    setPendingPurchase(true);
    router.push({ pathname: '/auth/mobile', params: { next: 'email' } });
  }, [router]);

  return (
    <YStack flex={1} bg={'$color.background'}>
      <Head>
        <title>Confirm purchase · Putting Scene</title>
      </Head>
      <Stack.Screen
        options={{
          headerTransparent: true,
          headerTitle: () => <></>,
          headerLeft: () => <PSHeaderBackButton variant="light" />,
          unstable_headerLeftItems: () => [
            {
              type: 'custom',
              element: <PSHeaderBackButton variant="light" />,
              hidesSharedBackground: true,
            },
          ],
        }}
      />

      {plan && gate ? (
        <>
          <PlanBand plan={plan} />

          {/* The band already clears the header, so the scroll mustn't. */}
          <PageScrollView headerTransparent={false}>
            <YStack gap={'$space.xl'}>
              <Pricing plan={plan} />

              {plan.planFeatures.length > 0 && (
                <>
                  <Divider />
                  <YStack gap={'$space.md'}>
                    <PSText.H2 color={'$color.textDark'}>
                      What you get
                    </PSText.H2>
                    {plan.planFeatures
                      .filter((p) => p.limit !== 0)
                      .map((planFeature) => (
                        <YStack key={planFeature.id} gap={'$space.xs'}>
                          <Row
                            label={planFeature.feature.name}
                            value={limitLabel(planFeature.limit)}
                          />
                          {planFeature.feature.description ? (
                            <PSText.Body4 color={'$color.textDarkMuted'}>
                              {planFeature.feature.description}
                            </PSText.Body4>
                          ) : null}
                        </YStack>
                      ))}
                  </YStack>
                </>
              )}

              {user && (
                <>
                  <Divider />
                  <YourDetails user={user} />
                </>
              )}

              <Notice plan={plan} gate={gate} />
            </YStack>
          </PageScrollView>

          {isPurchasable !== null && (
            <PurchaseBar
              plan={plan}
              gate={gate}
              isPaying={isPaying}
              onPay={handlePay}
              onLogin={handleLogin}
            />
          )}

          {blocked && (
            <PSModal
              visible
              title={blocked.title}
              message={blocked.message}
              ctaTitle={blocked.pendingId ? 'Continue' : 'Go to home'}
              onPressCta={() =>
                blocked.pendingId
                  ? router.replace(`/my-memberships/${blocked.pendingId}`)
                  : router.dismissTo('/')
              }
            />
          )}
        </>
      ) : (
        <StatusMessage>
          {isLoading
            ? 'Loading…'
            : (error?.message ?? 'This membership is no longer available.')}
        </StatusMessage>
      )}
    </YStack>
  );
}

/** Pinned above the scroll, reaching up under the transparent header. */
function PlanBand({ plan }: { plan: Membership }) {
  const headerHeight = useHeaderHeight();
  return (
    <YStack
      pt={headerHeight + space.xl}
      px={PAGE_GUTTER}
      pb={'$space.xl'}
      bg={'$color.brandDark'}
      borderBottomLeftRadius={'$radius.base'}
      borderBottomRightRadius={'$radius.base'}
    >
      <ConfirmationPagePlanCard plan={plan} />
    </YStack>
  );
}

function Pricing({ plan }: { plan: Membership }) {
  const prorated = isProrated(plan);
  const nothingDue = isNothingDueToday(plan);
  const days = remainingDaysInMonth();

  return (
    <YStack gap={'$space.md'}>
      <PSText.H2 color={'$color.textDark'}>Pricing</PSText.H2>

      {!nothingDue && (
        <YStack gap={'$space.sm'}>
          <Row
            label={prorated ? 'Payable this month' : 'Membership price'}
            value={(plan.proratedBasePrice ?? plan.basePrice).display}
          />
          <Row
            label={taxLabel(plan)}
            value={(plan.proratedTaxAmount ?? plan.taxAmount).display}
          />
          <XStack gap={'$space.md'}>
            <YStack flex={1}>
              <PSText.Body2 color={'$color.textDark'}>
                Total payable
              </PSText.Body2>
              {prorated && (
                <PSText.Body4 color={'$color.textDarkMuted'}>
                  for remaining {days} {days === 1 ? 'day' : 'days'} of this
                  month.
                </PSText.Body4>
              )}
            </YStack>
            <PSText.Body2 color={'$color.textDark'}>
              {(plan.proratedPriceWithTax ?? plan.priceWithTax).display}
            </PSText.Body2>
          </XStack>
        </YStack>
      )}

      {prorated && (
        <>
          <Callout
            message={
              nothingDue
                ? NOTHING_DUE_NOTICE
                : `Full price of the membership will be charged from ${nextCycleStart()}, unless you cancel ahead of time.`
            }
          />
          <Row
            label={`Total from ${nextCycleStart()}`}
            value={plan.priceWithTax.display}
          />
        </>
      )}
    </YStack>
  );
}

function YourDetails({ user }: { user: UserProfile }) {
  return (
    <YStack gap={'$space.lg'}>
      <PSText.H2 color={'$color.textDark'}>Your details</PSText.H2>
      <YStack gap={'$space.sm'}>
        {user.fullName ? (
          <DetailLine iconName="person-outline" value={user.fullName} />
        ) : null}
        {user.email ? (
          <DetailLine iconName="mail-outline" value={user.email} />
        ) : null}
        <DetailLine iconName="call-outline" value={user.mobile} />
      </YStack>
    </YStack>
  );
}

/** What buying does to what they already hold — see [[noticeFor]]. */
function Notice({ plan, gate }: { plan: Membership; gate: PurchaseGate }) {
  const notice = noticeFor(plan, gate);
  return notice ? <Callout message={notice} /> : null;
}

function PurchaseBar({
  plan,
  gate,
  isPaying,
  onPay,
  onLogin,
}: {
  plan: Membership;
  gate: PurchaseGate;
  isPaying: boolean;
  onPay: () => void;
  onLogin: () => void;
}) {
  if (gate.kind !== 'signed-out' && !canPurchase(gate)) return null;

  // A "₹0" here reads as a bug, so the press goes full width instead.
  const nothingDue = isNothingDueToday(plan);

  return (
    <PSBottomBar>
      {!nothingDue && (
        <YStack>
          <PSText.H2 color={'$color.textDark'}>
            {(plan.proratedPriceWithTax ?? plan.priceWithTax).display}
          </PSText.H2>
          <PSText.Body4 color={'$color.textDarkMuted'}>
            1 {plan.name}
          </PSText.Body4>
        </YStack>
      )}

      {gate.kind === 'signed-out' ? (
        <PSButton title="Login to purchase" onPress={onLogin} />
      ) : (
        <PSButton
          fullWidth={nothingDue}
          title={gate.kind === 'renew' ? 'Restart now' : 'Buy now'}
          loading={isPaying}
          loadingTitle="Caramelizing"
          onPress={onPay}
        />
      )}
    </PSBottomBar>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <XStack justify="space-between" gap={'$space.md'}>
      <PSText.Body3 flex={1} color={'$color.textDark'}>
        {label}
      </PSText.Body3>
      <PSText.Body3 color={'$color.textDarkMuted'}>{value}</PSText.Body3>
    </XStack>
  );
}

function DetailLine({
  iconName,
  value,
}: {
  iconName: ComponentProps<typeof PSIcon>['name'];
  value: string;
}) {
  return (
    <XStack items="center" gap={'$space.xs'}>
      <PSIcon name={iconName} size={16} color={'$textDarkMuted'} />
      <PSText.Body3 flex={1} numberOfLines={1} color={'$color.textDarkMuted'}>
        {value}
      </PSText.Body3>
    </XStack>
  );
}

function Callout({ message }: { message: string }) {
  return (
    <YStack
      bg={'$color.backgroundSecondary'}
      rounded={'$radius.base'}
      p={'$space.md'}
    >
      <PSText.Body3 color={'$color.textDark'}>{message}</PSText.Body3>
    </YStack>
  );
}

/**
 * The sentence the page owes this user — what buying does to what they hold.
 * It sits at the END of the content, right above the bar. The gates with nothing to buy get the screen's modal instead.
 */
const noticeFor = (plan: Membership, gate: PurchaseGate): string | null => {
  switch (gate.kind) {
    case 'upgrade':
      return `You're already a ${gate.from.plan.name} member. By purchasing this, you'll become a ${plan.name} member, effective immediately.`;
    case 'downgrade':
      return `You're a ${gate.from.plan.name} member. By purchasing this, you'll be downgraded to ${plan.name} starting ${downgradeStartsOn(gate.startsOn)}.`;
    case 'renew':
      // The pricing callout already covers it: they're restarting a month paid for.
      if (isNothingDueToday(plan)) return null;
      return gate.from.expiresAt
        ? `Your ${gate.from.plan.name} expires on ${formatDateWithYear(gate.from.expiresAt)}. Purchase this to stay a member.`
        : `Your ${gate.from.plan.name} is ending. Purchase this to stay a member.`;
    default:
      return null;
  }
};

/**
 * When the cheaper plan takes over: the end of the cycle they've already paid
 * for. `renewsAt` IS that date and it's the server's own figure, so it wins;
 * the fallback is the same rule the server follows (cycles are calendar-month
 * aligned) for the memberships that don't report one.
 */
const downgradeStartsOn = (renewsAt: string | null) =>
  formatDateWithYear(
    renewsAt ?? startOfMonth(addMonths(new Date(), 1)).toISOString()
  );

/**
 * The rate in a tax row's label. Null tax = no profile applies, and then the
 * amount beside it is zero — so the label says "Tax" rather than "Tax (0%)".
 *
 * `Number` is what drops the wire's trailing zeros ("18.00" → 18); a real
 * fractional rate keeps them ("18.50" → 18.5).
 */
const taxLabel = (plan: Membership) =>
  plan.tax ? `Tax (${Number(plan.tax.totalPercentage)}%)` : 'Tax';

/**
 * How many days this month's part-charge covers, TODAY INCLUDED — the rule the
 * server prices by (its own `days_charged` lands on the created membership, long
 * after this screen needs the number).
 */
const remainingDaysInMonth = () => {
  const today = new Date();
  return differenceInCalendarDays(endOfMonth(today), today) + 1;
};

const NOTHING_DUE_NOTICE =
  "You've already paid for this month, so you'll just need to set up autopay to restart your membership.";

/** When the full price starts: the 1st of next month, cycles being calendar-aligned. */
const nextCycleStart = () =>
  format(startOfMonth(addMonths(new Date(), 1)), 'do MMM');

/**
 * Why this user can't buy this plan, for the modal that blocks the page — null
 * whenever the press can still go through (signed out included: logging in is
 * what decides).
 */
const blockedNoticeFor = (
  plan: Membership,
  gate: PurchaseGate
): { title: string; message: string; pendingId?: string } | null => {
  switch (gate.kind) {
    case 'trial-ineligible':
      return {
        title: `You're not eligible for ${plan.name}`,
        message: `${plan.name} is only for first-time members.`,
      };
    case 'already-member':
      return {
        title: "You're already a member",
        message: `You're already on ${gate.from.plan.name}, so there's nothing to buy here.`,
      };
    case 'confirming':
      return {
        title: 'Your payment is pending',
        message:
          'The payment for a membership is pending. Confirm or cancel it before buying another.',
        pendingId: gate.pendingId,
      };
    default:
      return null;
  }
};

function StatusMessage({ children }: { children: string }) {
  return (
    <YStack flex={1} items="center" justify="center" p={'$space.xl'}>
      <PSText.Body2 color={'$color.textDarkMuted'} text="center">
        {children}
      </PSText.Body2>
    </YStack>
  );
}
